/** client/src/components/RouteGuard.tsx — Smart-bounce wrapper enforcing per-route context. */

import { useEffect } from "react";
import type { ReactNode } from "react";
import { useLocation } from "wouter";
import { useOrgContext } from "@/lib/use-org-context";
import { getRouteContext } from "@/lib/route-context";
import type { RouteContext } from "@/lib/route-context";

// ---------------------------------------------------------------------------
// Compatibility helper
// ---------------------------------------------------------------------------

/**
 * Returns true when the route's required context is satisfied by the active
 * context. "both" routes always pass. Inline for readability — no branching
 * surprises.
 */
function isCompatible(
  routeContext: RouteContext,
  currentContext: "personal" | "org",
): boolean {
  if (routeContext === "both") return true;
  if (routeContext === "personal-only") return currentContext === "personal";
  if (routeContext === "org-only") return currentContext === "org";
  return false; // unreachable — satisfies exhaustiveness
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface RouteGuardProps {
  children: ReactNode;
}

/**
 * <RouteGuard> — wraps a route subtree and redirects to /dashboard when the
 * current org context is incompatible with the route's required scope.
 *
 * Design choices:
 * - **Effect-only** approach (no render-time short-circuit). The one-frame
 *   flash on an incompatible route is acceptable and keeps the render path
 *   simple. A render-time short-circuit would duplicate the compatibility
 *   check and introduce a subtle ordering issue with React's double-invoke
 *   in StrictMode. Tests confirmed the effect fires reliably after a single
 *   act() flush.
 * - Effect deps: [location, activeOrgId, isLoading]. `currentContext` is
 *   derived from `activeOrgId` inside the provider, so depending on
 *   `activeOrgId` is equivalent and avoids a stale-closure risk if
 *   `currentContext` were to diverge.
 */
export function RouteGuard({ children }: RouteGuardProps) {
  const [location, setLocation] = useLocation();
  const { currentContext, activeOrgId, isLoading } = useOrgContext();

  useEffect(() => {
    // Suppress bounce while the initial membership fetch is in-flight.
    // This prevents a flicker where a personal-context user is briefly
    // bounced away from a personal-only route before memberships load.
    if (isLoading) return;

    const routeCtx = getRouteContext(location);
    if (!isCompatible(routeCtx, currentContext)) {
      setLocation("/dashboard");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location, activeOrgId, isLoading]);

  return <>{children}</>;
}
